import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { RecordCard } from "../components/records/RecordCard";
import { FiArrowLeft } from "react-icons/fi";
import { ParsedRecord } from "../types/records";
import { timestampService } from "../services/api/timestampService";
import { Timestamp } from "../types/api";
import { safeFormatDate } from "../utils/dateUtils";
import { EmptyState } from "../components/records/EmptyState";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
  min-height: 100vh;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 24px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60vh;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 60vh;
  text-align: center;

  h3 {
    font-size: 24px;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    max-width: 500px;
  }
`;

const RecordDetailView: React.FC = () => {
  const { recordHash } = useParams<{ recordHash: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<ParsedRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper function to check if a string is valid JSON
  const isValidJSON = useCallback((str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // Function to decode and parse timestamp data
  const getTimestampData = useCallback(
    (timestamp: Timestamp) => {
      try {
        const decodedData = atob(timestamp.data);

        if (isValidJSON(decodedData)) {
          const data = JSON.parse(decodedData);
          return data;
        }
      } catch (error) {
        console.error("Error processing timestamp:", error);
      }

      return null;
    },
    [isValidJSON]
  );

  // Function to convert timestamp to parsed record
  const convertTimestampToRecord = useCallback(
    (timestamp: Timestamp): ParsedRecord | null => {
      try {
        const data = getTimestampData(timestamp);

        if (!data) {
          return null;
        }

        // Get timestamp (safari compatible)
        let formattedTimestamp =
          data.timestamp || data.date || timestamp.timestamp;
        formattedTimestamp = safeFormatDate(formattedTimestamp);

        // Safely format blockchain timestamp (safari compatible)
        const blockchainTimestamp = safeFormatDate(timestamp.timestamp);

        // Create a ParsedRecord from the timestamp data
        return {
          hash: data.hash,
          image: data.image,
          deviceInfo: data.deviceInfo || data,
          timestamp: formattedTimestamp,
          originalTimestamp: timestamp.timestamp,
          blockchainData: {
            index: timestamp.index,
            timestamp: blockchainTimestamp,
            hash: timestamp.hash,
          },
        };
      } catch (error) {
        console.error("Error converting timestamp to record:", error);
        return null;
      }
    },
    [getTimestampData]
  );

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        setLoading(true);

        // Get the full hash from the URL and decode it to handle special characters
        const fullPath = window.location.pathname;
        const encodedHash = fullPath.substring(
          fullPath.indexOf("/record/") + 8
        );
        const fullHash = decodeURIComponent(encodedHash);

        if (!fullHash) {
          setError("No record hash provided");
          setLoading(false);
          return;
        }

        const timestamp = await timestampService.getTimestampByHash(fullHash);

        if (!timestamp) {
          setError("Record not found");
          setLoading(false);
          return;
        }

        // Convert the timestamp to a parsed record
        const parsedRecord = convertTimestampToRecord(timestamp);

        if (parsedRecord) {
          setRecord(parsedRecord);
        } else {
          setError("Failed to parse record data");
        }
      } catch (error) {
        console.error("Error fetching record:", error);
        setError("Failed to load record details");
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [recordHash, convertTimestampToRecord]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString();
  };

  const formatKey = (key: string) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <Container>
      <BackButton onClick={handleGoBack}>
        <FiArrowLeft /> Back
      </BackButton>

      {loading ? (
        <LoadingContainer>Loading record details...</LoadingContainer>
      ) : error ? (
        <EmptyState title="Error Loading Record" message={error} />
      ) : record ? (
        <RecordCard
          record={record}
          index={0}
          formatDate={formatDate}
          formatKey={formatKey}
          blockchainData={record.blockchainData}
          isExpanded={true}
        />
      ) : (
        <ErrorContainer>
          <h3>Record Not Found</h3>
          <p>The requested record could not be found or loaded.</p>
        </ErrorContainer>
      )}
    </Container>
  );
};

export default RecordDetailView;
